import {
  collection,
  doc,
  increment,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  type FirestoreDataConverter,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { withUpdatedAt } from "@/helpers/firestore/DeltaSyncService";
import { v1 as uuidv1 } from "uuid";
import type { Comment } from "@kekkle/shared/types/fields/comment/comment";

type FetchCommentsOptions = {
  groupId: string;
  itemId: string;
  collectionName: string;
  converter: FirestoreDataConverter<any>;
};

type PostCommentOptions = {
  groupId: string;
  itemId: string;
  collectionName: string;
  commentText: string;
  authorUuid: string;
  incrementField?: string;
  /**
   * Stamp `updated_at` on the item together with the counter. Required for
   * delta-synced collections (quotes, photos), or other devices never see
   * the new count. Dilemma rules reject the extra field.
   */
  stampUpdatedAt?: boolean;
};

/**
 * Subscribes to comments for a specific item (photo, quote, or event).
 * Uses onSnapshot for real-time updates.
 * Returns an unsubscribe function.
 */
export function subscribeToComments(
  options: FetchCommentsOptions,
  callbacks: {
    onComments: (comments: Comment[]) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  const commentsRef = doc(
    collection(
      firestore,
      `groups/${options.groupId}/${options.collectionName}`,
    ),
    options.itemId,
  ).withConverter(options.converter);

  return onSnapshot(
    commentsRef,
    (snapshot) => {
      //TODO Adjust convertor to include timestamp estimates
      // Skip snapshots with pending writes — serverTimestamp() fields are null
      // until the server confirms, which crashes the converter's .toDate() call
      if (snapshot.metadata.hasPendingWrites) return;

      if (!snapshot.exists()) {
        callbacks.onComments([]);
        return;
      }

      const data = snapshot.data();
      const { _id, affected_field, ...commentEntries } = data;

      const arrayWithComments = (Object.values(commentEntries) as Comment[])
        .filter((c) => c.created_at != null)
        .sort((a, b) => a.created_at.getTime() - b.created_at.getTime());

      callbacks.onComments(arrayWithComments);
    },
    (error) => {
      callbacks.onError(error);
    },
  );
}

/**
 * Posts a new comment to a specific item (photo, quote, or event)
 * Optionally increments a counter field on the parent item
 */
export async function postComment(
  options: PostCommentOptions,
): Promise<Comment> {
  try {
    const batch = writeBatch(firestore);

    const commentDoc = doc(
      collection(
        firestore,
        `groups/${options.groupId}/${options.collectionName}`,
      ),
      options.itemId,
    );

    const id = uuidv1();

    batch.set(
      commentDoc,
      {
        [id]: {
          comment: options.commentText,
          author: options.authorUuid,
          created_at: serverTimestamp(),
        },
        affected_field: id,
      },
      { merge: true },
    );

    // If incrementField is provided, increment the counter on the item
    if (options.incrementField) {
      const itemCollectionName = options.collectionName.replace(
        "_comments",
        "",
      );
      const itemRef = doc(
        collection(
          firestore,
          `groups/${options.groupId}/${itemCollectionName}`,
        ),
        options.itemId,
      );
      const counterUpdate = { [options.incrementField]: increment(1) };
      batch.update(
        itemRef,
        options.stampUpdatedAt ? withUpdatedAt(counterUpdate) : counterUpdate,
      );
    }

    await batch.commit();

    // Return the new comment with current date for immediate UI feedback
    return {
      comment: options.commentText,
      author: options.authorUuid,
      created_at: new Date(),
    };
  } catch (error) {
    handleFirestoreException({ e: error as FirestoreError });
    throw error;
  }
}
